// Mi Clínica: panel de configuración del centro médico (Centro Médico → Catálogos).
// Muestra el perfil de la empresa, indicadores del catálogo clínico y los
// convenios vigentes (gestión completa en Catálogos → Convenios).
import { useQuery } from '@tanstack/react-query';
import { Activity, Building, Building2, Loader2, Stethoscope, UserRound, Users } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { useAuth } from '@/lib/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Convenio, Especialidad, MedicoRow, PacienteRow, TIPOS_CONVENIO, med, medRpc } from './shared';

interface EmpresaRow { nombre: string; ruc: string | null; direccion: string | null; telefono: string | null; email: string | null; logo: string | null }
const tipoLabel = (t: string) => TIPOS_CONVENIO.find((x) => x.v === t)?.label ?? t;

export function MiClinica() {
  const { user } = useAuth();
  const empresaId = user?.empresa?.id;

  const { data: empresa } = useQuery<EmpresaRow | null>({
    queryKey: ['mi-empresa', empresaId],
    queryFn: async () => (await apiClient.get<EmpresaRow[]>(`/rest/autenticacion.empresas?id=eq.${empresaId}&select=nombre,ruc,direccion,telefono,email,logo`)).data[0] ?? null,
    enabled: !!empresaId,
  });
  const { data: medicos = [] } = useQuery<MedicoRow[]>({ queryKey: ['med-medicos', ''], queryFn: () => medRpc<MedicoRow[]>('med_medicos', { _busqueda: '' }) });
  const { data: especialidades = [] } = useQuery<Especialidad[]>({ queryKey: ['med-especialidades'], queryFn: () => med.list<Especialidad>('especialidad', '?select=*&deleted_at=is.null') });
  const { data: pacientes = [] } = useQuery<PacienteRow[]>({ queryKey: ['med-pacientes', ''], queryFn: () => medRpc<PacienteRow[]>('med_pacientes', { _busqueda: '' }) });
  const { data: convenios = [], isFetching } = useQuery<Convenio[]>({ queryKey: ['med-convenios', 'todos'], queryFn: () => medRpc<Convenio[]>('med_convenios', { _tipo: null }) });

  const emp = empresa ?? { nombre: user?.empresa?.nombre ?? '', ruc: null, direccion: null, telefono: null, email: null, logo: user?.empresa?.logo ?? null };
  const conveniosActivos = convenios.filter((c) => c.es_activo);

  const stats = [
    { label: 'Médicos', valor: medicos.length, icon: Stethoscope },
    { label: 'Especialidades', valor: especialidades.length, icon: Activity },
    { label: 'Pacientes', valor: pacientes.length, icon: UserRound },
    { label: 'Convenios activos', valor: conveniosActivos.length, icon: Users },
  ];

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Building className="w-5 h-5" /> Mi Clínica</h1>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-4 p-4">
          {emp.logo ? <img src={emp.logo} alt="" className="h-20 w-20 rounded-md border object-contain bg-background" />
            : <div className="flex h-20 w-20 items-center justify-center rounded-md border bg-muted text-muted-foreground"><Building2 className="h-8 w-8" /></div>}
          <div className="space-y-0.5">
            <h2 className="text-lg font-bold">{emp.nombre || '—'}</h2>
            {emp.ruc && <p className="text-sm text-muted-foreground">RUC: {emp.ruc}</p>}
            {emp.direccion && <p className="text-sm text-muted-foreground">{emp.direccion}</p>}
            <p className="text-sm text-muted-foreground">{[emp.telefono, emp.email].filter(Boolean).join(' · ') || 'Sin datos de contacto'}</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map((s) => (
          <Card key={s.label}>
            <CardContent className="flex items-center gap-3 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary"><s.icon className="h-5 w-5" /></div>
              <div><div className="text-2xl font-bold tabular-nums">{s.valor}</div><div className="text-xs text-muted-foreground">{s.label}</div></div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Convenios ({convenios.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Razón social</th><th className="py-2 pr-3">Tipo</th>
              <th className="py-2 pr-3 text-right">Comisión %</th><th className="py-2 pr-3">Contacto</th><th className="py-2 pr-3">Estado</th>
            </tr></thead>
            <tbody>
              {convenios.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin convenios. Créalos en Catálogos → Convenios.</td></tr>}
              {convenios.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{c.razon_social}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{tipoLabel(c.tipo)}</span></td>
                  <td className="py-2 pr-3 text-right tabular-nums">{Number(c.comision_pct)}%</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{c.contacto_nombre ?? '—'}{c.contacto_telefono ? ` · ${c.contacto_telefono}` : ''}</td>
                  <td className="py-2 pr-3">{c.es_activo ? 'Activo' : <span className="text-muted-foreground">Inactivo</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-muted-foreground">La gestión de convenios (alta/edición) está en Catálogos → Convenios.</p>
        </CardContent>
      </Card>
    </div>
  );
}
